/**
 * EventOrgTags — tag an event to churches and/or provinces.
 *
 * Whoever is tagged sees the event in their own workspace Events list. A super
 * or province admin can tag any unit they reach; a church admin only their own.
 * Units outside the editor's reach are shown locked and are preserved on save.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Search, Church, Landmark, Lock } from 'lucide-react';
import { eventAPI, churchesAPI, getErrorMessage } from '@/lib/api';

const GROUPING_TYPES = ['hq', 'headquarters', 'country', 'national', 'province',
  'region', 'zone', 'district'];

const typeLabel = (t) => {
  const v = (t || '').toLowerCase();
  if (!v) return 'Unit';
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const EventOrgTags = ({ eventId }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const { data: tags, isLoading: loadingTags } = useQuery({
    queryKey: ['event-org-tags', eventId],
    queryFn: () => eventAPI.getOrgTags(eventId).then((r) => r.data),
    enabled: !!eventId,
  });

  const { data: units, isLoading: loadingUnits } = useQuery({
    queryKey: ['churches-for-tagging'],
    queryFn: () => churchesAPI.adminList().then((r) => r.data),
  });

  const chosen = selected ?? (tags?.unit_ids || []);
  const editable = tags?.can_tag_all ? null : new Set(tags?.editable_unit_ids || []);
  const canEdit = (id) => !editable || editable.has(id);

  const save = useMutation({
    mutationFn: () => eventAPI.updateOrgTags(eventId, chosen),
    onSuccess: () => {
      toast.success('Event visibility updated');
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['event-org-tags', eventId] });
      // Every church, assembly and province feed that could now include (or
      // have lost) this event is refetched at once — no page rebuild, no
      // manual sync, and an already-open page catches up on its next focus.
      qc.invalidateQueries({
        predicate: (q) => {
          const key = JSON.stringify(q.queryKey || []).toLowerCase();
          return key.includes('event') || key.includes('church');
        },
      });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save tags')),
  });

  const { groupings, congregations } = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = (units || []).filter((u) => !term
      || (u.name || '').toLowerCase().includes(term)
      || (u.code || '').toLowerCase().includes(term)
      || (u.city || '').toLowerCase().includes(term)
      || (u.location || '').toLowerCase().includes(term)
      || (u.country || '').toLowerCase().includes(term));
    const isGrouping = (u) => GROUPING_TYPES.includes((u.organisation_type || u.level || '').toLowerCase());
    return {
      groupings: rows.filter(isGrouping),
      congregations: rows.filter((u) => !isGrouping(u)),
    };
  }, [units, search]);

  const toggle = (id) => {
    if (!canEdit(id)) return;
    setSelected(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);
  };

  const Row = ({ unit }) => {
    const id = unit.id;
    const locked = !canEdit(id);
    return (
      <label
        className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
          locked ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
            : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 cursor-pointer'}`}
        data-testid={`event-tag-unit-${id}`}
      >
        <Checkbox
          checked={chosen.includes(id)}
          disabled={locked}
          onCheckedChange={() => toggle(id)}
          data-testid={`event-tag-checkbox-${id}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-slate-900 truncate">{unit.name}</span>
          <span className="block text-[11px] text-slate-500 truncate">
            {[typeLabel(unit.organisation_type || unit.level), unit.city, unit.country]
              .filter(Boolean).join(' · ')}
          </span>
        </span>
        {locked && <Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
      </label>
    );
  };

  if (loadingTags || loadingUnits) {
    return (
      <div className="py-20 text-center" data-testid="event-org-tags-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
      </div>
    );
  }

  return (
    <Card data-testid="event-org-tags">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Church className="h-5 w-5 text-emerald-600" />
          Churches &amp; Provinces
        </CardTitle>
        <CardDescription>
          Tag this event so it appears in the Events list of each church or province you pick.
          {tags?.can_tag_all
            ? ' You can tag any unit on the platform.'
            : ' You can only tag units inside your own scope.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" data-testid="event-tag-count">
            {chosen.length} tagged
          </Badge>
          {(tags?.units || []).slice(0, 6).map((u) => (
            <Badge key={u.id} variant="outline" className="font-normal">{u.name}</Badge>
          ))}
        </div>

        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by province, assembly, code, city…"
            className="pl-9"
            data-testid="event-tag-search"
          />
        </div>

        {groupings.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <Landmark className="h-3.5 w-3.5" /> Provinces, districts &amp; national offices
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {groupings.map((u) => <Row key={u.id} unit={u} />)}
            </div>
            <p className="text-[11px] text-slate-500">
              Tagging a province shows the event to every district and assembly beneath it.
            </p>
          </div>
        )}

        {congregations.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <Church className="h-3.5 w-3.5" /> Churches &amp; assemblies
            </p>
            <div className="grid gap-2 sm:grid-cols-2 max-h-96 overflow-y-auto pr-1">
              {congregations.map((u) => <Row key={u.id} unit={u} />)}
            </div>
          </div>
        )}

        {!groupings.length && !congregations.length && (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="event-tag-empty">
            No churches match that search.
          </p>
        )}

        <div className="flex items-center gap-2 pt-2 border-t">
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || selected === null}
            data-testid="event-tag-save"
          >
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save visibility'}
          </Button>
          {selected !== null && (
            <Button variant="ghost" onClick={() => setSelected(null)} data-testid="event-tag-reset">
              Cancel
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EventOrgTags;
